import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  FaPlus, FaThumbtack, FaArchive, FaTrash, FaSearch,
  FaCheck, FaTimes, FaClipboardList, FaChevronRight, FaEdit,
  FaEllipsisV, FaChevronDown, FaChevronLeft, FaArrowLeft,
  FaMinus, FaCopy, FaSave, FaGripVertical
} from 'react-icons/fa';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API_BASE_URL } from '../utils/constants';
import { formatDateRelative } from '../utils/date';
import session from '../utils/session';
import useConfirm from '../hooks/useConfirm';
import { useLanguage } from '../context/LanguageContext';
import showToast from '../utils/toast';
import Modal from '../components/Modal/Modal';
import PageHero from '../components/PageHero/PageHero';
import offlineStorage from '../offline/OfflineStorage';
import onlineDetector from '../offline/OnlineDetector';
import syncEngine from '../offline/SyncEngine';
import './Checklist.scss';

const GROUP_ICONS = [
  '📷', '🔋', '🎥', '📦', '📄', '🎵', '💡', '🪑', '🎂', '🌸', '📋', '⚡',
  '🔧', '🛠️', '🔨', '🪛', '🧰', '📐',
  '🎀', '🎈', '🎊', '🎉', '✨', '🎭',
  '🍽️', '🍴', '🥂', '🍷', '🧊', '🔥',
  '🚗', '🚐', '🚚', '🅿️', '📍',
  '👗', '👔', '💍', '💐', '🕯️',
  '📺', '🔊', '🎤', '🎹', '🎸', '💃',
  '🏠', '🏛️', '🌳', '🏖️', '⛪',
  '📝', '🖊️', '📎', '📌', '🗂️', '📅',
  '🧾', '💳', '💰', '🏷️', '📊', '✅',
];

// Helper to convert offline record to checklist object
const offlineRecordToChecklist = (record) => {
  const checklist = { ...record.data };
  checklist._id = checklist._id || record.serverId || record.localId;
  if (!checklist.updatedAt && record.updatedAt) checklist.updatedAt = record.updatedAt;
  return checklist;
};

// Merge all local IndexedDB checklists missing from the server response
const ensureOfflineChecklists = async (currentChecklists) => {
  try {
    const records = await offlineStorage.getAll('Checklist');
    if (records.length === 0) return currentChecklists;
    const localChecklists = records.map(offlineRecordToChecklist);
    const existingIds = new Set(currentChecklists.map(c => c._id));
    const missing = localChecklists.filter(c => !existingIds.has(c._id));
    if (missing.length === 0) return currentChecklists;
    const merged = [...currentChecklists, ...missing];
    merged.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
    });
    return merged;
  } catch {
    return currentChecklists;
  }
};

// Draggable wrapper for a checklist group (moves the whole group with its items)
const SortableGroupShell = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`checklist-detail__group${isDragging ? ' checklist-detail__group--dragging' : ''}`}
    >
      {typeof children === 'function' ? children({ attributes, listeners }) : children}
    </div>
  );
};

// Draggable wrapper for a checklist item
const SortableItemShell = ({ id, className, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${className}${isDragging ? ' checklist-detail__item--dragging' : ''}`}
    >
      {typeof children === 'function' ? children({ attributes, listeners }) : children}
    </div>
  );
};

const ChecklistList = () => {
  const { t } = useLanguage();
  const confirm = useConfirm();

  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '' });

  // Rename modal
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameTitle, setRenameTitle] = useState('');

  // View modal (detail view)
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedChecklist, setSelectedChecklist] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Group modal
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', icon: '' });

  // Item modal
  const [showItemModal, setShowItemModal] = useState(false);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({ name: '', qty: 1, note: '' });

  const getToken = () => session.getToken();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const fetchChecklists = useCallback(async () => {
    // Always try loading from IndexedDB first for instant display
    let cachedChecklists = [];
    try {
      const records = await offlineStorage.getAll('Checklist');
      cachedChecklists = records.map(offlineRecordToChecklist).filter(c => {
        const archived = c.archived === undefined ? false : c.archived;
        return archived === showArchived;
      });
      cachedChecklists.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
      });
    } catch (e) {
      console.error('Error loading from IndexedDB:', e);
    }

    // Show cached data immediately (no loading spinner)
    if (cachedChecklists.length > 0) {
      setChecklists(cachedChecklists);
      setLoading(false);
    }

    // Then try fetching from server
    try {
      const token = getToken();
      const params = new URLSearchParams({ archived: showArchived });
      const res = await fetch(`${API_BASE_URL}/checklists?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        offlineStorage.cacheServerData('Checklist', data).catch(() => {});
        const merged = await ensureOfflineChecklists(data);
        // Only show checklists matching the currently active tab. Locally
        // cached records can have a stale archived flag (e.g. empty server
        // response, offline full-cache fallback), so re-filter after merging.
        const tabChecklists = merged.filter(c => {
          const archived = c.archived === undefined ? false : c.archived;
          return archived === showArchived;
        });
        setChecklists(tabChecklists);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.error('Error fetching checklists from server:', err);
    }

    // If no cached data and server failed, show empty
    if (cachedChecklists.length === 0) {
      setChecklists([]);
    }
    setLoading(false);
  }, [showArchived]);

  const location = useLocation();
  const pendingChecklistIdRef = useRef(null);
  const [highlightedChecklistId, setHighlightedChecklistId] = useState(null);

  // Deep-link for push clicks when app was closed (SW opens /checklists?id=...)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const c = checklists.find(x => String(x._id) === String(qId));
        if (c) {
          setHighlightedChecklistId(String(qId));
          openViewModal(c);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } else {
          pendingChecklistIdRef.current = String(qId);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pendingChecklistIdRef.current && checklists.length) {
      const c = checklists.find(x => String(x._id) === String(pendingChecklistIdRef.current));
      if (c) {
        setHighlightedChecklistId(pendingChecklistIdRef.current);
        openViewModal(c);
        pendingChecklistIdRef.current = null;
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        } catch (e) {}
      }
    }
  }, [checklists]);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const qId = params.get('id');
      if (qId) {
        const c = checklists.find(x => String(x._id) === String(qId));
        if (c) {
          setHighlightedChecklistId(String(qId));
          openViewModal(c);
          const url = new URL(window.location.href);
          url.searchParams.delete('id');
          window.history.replaceState({}, '', url.pathname + (url.search ? `?${url.searchParams.toString()}` : '') + url.hash);
        }
      }
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  useEffect(() => {
    const handler = (ev) => {
      try {
        const detail = ev && ev.detail ? ev.detail : null;
        if (!detail || detail.path !== '/checklists') return;
        const state = detail.state || {};
        if (state.openViewId) {
          const c = checklists.find(x => String(x._id) === String(state.openViewId));
          if (c) {
            setHighlightedChecklistId(String(state.openViewId));
            openViewModal(c);
          } else pendingChecklistIdRef.current = String(state.openViewId);
        }
      } catch (e) {}
    };
    window.addEventListener('ui:notification', handler);
    return () => window.removeEventListener('ui:notification', handler);
  }, [checklists]);

  useEffect(() => {
    fetchChecklists();
  }, [fetchChecklists]);

  // Listen for sync engine completion to refresh checklists
  useEffect(() => {
    const unsub = syncEngine.onSync((event) => {
      if (event.type === 'complete') {
        fetchChecklists();
      }
    });
    return unsub;
  }, [fetchChecklists]);

  const computeProgress = (checklist) => {
    const tot = checklist.groups?.reduce((s, g) => s + (g.items?.length || 0), 0) || 0;
    const comp = checklist.groups?.reduce((s, g) => s + (g.items?.filter(i => i.completed)?.length || 0), 0) || 0;
    return { tot, comp, pct: tot > 0 ? Math.round((comp / tot) * 100) : 0 };
  };

  const totalItems = selectedChecklist
    ? selectedChecklist.groups.reduce((sum, g) => sum + (g.items?.length || 0), 0)
    : 0;
  const completedItems = selectedChecklist
    ? selectedChecklist.groups.reduce((sum, g) => sum + (g.items?.filter(i => i.completed)?.length || 0), 0)
    : 0;

  // ─── Create ───────────────────────────────────────────────────

  const handleCreate = async () => {
    if (!createForm.title.trim()) { showToast('Title is required', 'error'); return; }

    if (!onlineDetector.isOnline) {
      showToast('Cannot create checklist while offline. Please reconnect.', 'error');
      return;
    }

    try {
      const token = getToken();
      const checklistData = { title: createForm.title.trim(), groups: [], archived: false, pinned: false };
      const res = await fetch(`${API_BASE_URL}/checklists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(checklistData),
      });
      if (!res.ok) throw new Error('Failed to create');
      const data = await res.json();
      setShowCreateModal(false);
      setCreateForm({ title: '' });
      setChecklists(prev => [data, ...prev]);
      try { window.dispatchEvent(new CustomEvent('notifications:refresh')); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),600); setTimeout(()=>window.dispatchEvent(new CustomEvent('notifications:refresh')),1600);} catch(e){}
      openViewModal(data);
    } catch (err) {
      console.error('Error creating checklist:', err);
    }
  };

  // ─── View Modal ───────────────────────────────────────────────

  const openViewModal = (checklist) => {
    setSelectedChecklist(checklist);
    setCollapsedGroups(new Set());
    setShowActionsMenu(false);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedChecklist(null);
  };

  const syncChecklist = async (id) => {
    // Try server first
    if (onlineDetector.isOnline) {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/checklists/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setSelectedChecklist(data);
          setChecklists(prev => prev.map(c => c._id === id ? data : c));
          return data;
        }
      } catch (err) {
        console.error('Error syncing checklist from server:', err);
      }
    }

    // Fallback to IndexedDB
    try {
      const record = await offlineStorage.getById('Checklist', id);
      if (record) {
        const data = offlineRecordToChecklist(record);
        setSelectedChecklist(data);
        setChecklists(prev => prev.map(c => c._id === id ? data : c));
        return data;
      }
    } catch (err) {
      console.error('Error syncing checklist from IndexedDB:', err);
    }
    return null;
  };

  // ─── Card actions ─────────────────────────────────────────────

  const handleDelete = async (id, e) => {
    if (e) e.stopPropagation();
    const confirmed = await confirm('Delete this checklist?', 'This cannot be undone.');
    if (!confirmed) return;

    // Optimistic local update
    setChecklists(prev => prev.filter(c => c._id !== id));
    if (selectedChecklist?._id === id) {
      setShowViewModal(false);
      setSelectedChecklist(null);
    }

    if (onlineDetector.isOnline) {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/checklists/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to delete');
        showToast('Deleted', 'success');
        return;
      } catch (err) {
        console.error('Error deleting checklist:', err);
        fetchChecklists();
        showToast('Failed to delete checklist', 'error');
        return;
      }
    }

    showToast('Cannot delete while offline. Please reconnect.', 'error');
    fetchChecklists();
  };

  const openRename = (id, currentTitle, e) => {
    e.stopPropagation();
    setRenamingId(id);
    setRenameTitle(currentTitle);
    setShowRenameModal(true);
  };

  const handleRename = async () => {
    if (!renameTitle.trim()) { showToast('Title is required', 'error'); return; }

    // Optimistic local update
    const updatedChecklist = { ...checklists.find(c => c._id === renamingId), title: renameTitle.trim(), updatedAt: Date.now() };
    setChecklists(prev => prev.map(c => c._id === renamingId ? updatedChecklist : c));
    setSelectedChecklist(prev => prev?._id === renamingId ? updatedChecklist : prev);
    setShowRenameModal(false);

    if (onlineDetector.isOnline) {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/checklists/${renamingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({ title: renameTitle.trim() }),
        });
        if (!res.ok) throw new Error('Failed to rename');
        const data = await res.json();
        setChecklists(prev => prev.map(c => c._id === renamingId ? data : c));
        setSelectedChecklist(prev => prev?._id === renamingId ? data : prev);
        showToast('Renamed', 'success');
        return;
      } catch (err) {
        console.error('Error renaming checklist:', err);
        syncChecklist(renamingId);
        showToast('Failed to rename checklist', 'error');
        return;
      }
    }

    showToast('Cannot rename while offline. Please reconnect.', 'error');
    syncChecklist(renamingId);
  };

  const sortChecklists = (list) => [...list].sort((a, b) => {
    if (a.pinned && !b.pinned) return -1;
    if (!a.pinned && b.pinned) return 1;
    return new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0);
  });

  const handlePinToggle = async (id, e) => {
    if (e?.stopPropagation) e.stopPropagation();

    // Find current checklist and toggle pinned status (fall back to the
    // open detail modal, which may hold a checklist filtered out of the list)
    const checklist = checklists.find(c => String(c._id) === String(id))
      || (selectedChecklist && String(selectedChecklist._id) === String(id) ? selectedChecklist : null);
    if (!checklist) return;
    const newPinned = !checklist.pinned;

    // Optimistic local update (re-sorted so pinned items jump to the top)
    const updatedChecklist = { ...checklist, pinned: newPinned, updatedAt: new Date().toISOString() };
    setChecklists(prev => sortChecklists(prev.map(c => String(c._id) === String(id) ? updatedChecklist : c)));
    setSelectedChecklist(prev => (prev && String(prev._id) === String(id) ? updatedChecklist : prev));

    if (onlineDetector.isOnline) {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/checklists/${id}/pin`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        const dataId = String(data._id || id);
        setChecklists(prev => sortChecklists(prev.map(c => String(c._id) === dataId ? data : c)));
        setSelectedChecklist(prev => (prev && String(prev._id) === dataId ? data : prev));
        showToast(data.pinned ? 'Pinned' : 'Unpinned', 'success');
        return;
      } catch (err) {
        console.error('Error toggling pin:', err);
        setChecklists(prev => sortChecklists(prev.map(c => String(c._id) === String(id) ? checklist : c)));
        setSelectedChecklist(prev => (prev && String(prev._id) === String(id) ? checklist : prev));
        showToast('Failed to toggle pin', 'error');
        return;
      }
    }

    showToast('Cannot pin/unpin while offline. Please reconnect.', 'error');
    setChecklists(prev => sortChecklists(prev.map(c => String(c._id) === String(id) ? checklist : c)));
    setSelectedChecklist(prev => (prev && String(prev._id) === String(id) ? checklist : prev));
  };

  const handleArchiveToggle = async (id, e) => {
    if (e?.stopPropagation) e.stopPropagation();

    // Find current checklist and toggle archived status
    const checklist = checklists.find(c => c._id === id);
    if (!checklist) return;
    const newArchived = !checklist.archived;

    // Optimistic local update
    const updatedChecklist = { ...checklist, archived: newArchived, updatedAt: Date.now() };
    setChecklists(prev => prev.map(c => c._id === id ? updatedChecklist : c));
    if (selectedChecklist?._id === id) setSelectedChecklist(updatedChecklist);

    if (onlineDetector.isOnline) {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/checklists/${id}/archive`, {
          method: 'PATCH',
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setChecklists(prev => prev.map(c => c._id === id ? data : c));
        if (selectedChecklist?._id === id) setSelectedChecklist(data);
        showToast(data.archived ? 'Archived' : 'Restored', 'success');
        fetchChecklists();
        return;
      } catch (err) {
        console.error('Error toggling archive:', err);
        setChecklists(prev => prev.map(c => c._id === id ? checklist : c));
        if (selectedChecklist?._id === id) setSelectedChecklist(checklist);
        showToast('Failed to toggle archive', 'error');
        return;
      }
    }

    showToast('Cannot archive/restore while offline. Please reconnect.', 'error');
    setChecklists(prev => prev.map(c => c._id === id ? checklist : c));
    if (selectedChecklist?._id === id) setSelectedChecklist(checklist);
  };

  const doSaveChecklist = async (updates) => {
    if (!selectedChecklist) return;

    // Optimistic local update
    const updatedChecklist = { ...selectedChecklist, ...updates, updatedAt: Date.now() };
    setSelectedChecklist(updatedChecklist);
    setChecklists(prev => prev.map(c => c._id === updatedChecklist._id ? updatedChecklist : c));

    if (onlineDetector.isOnline) {
      try {
        const token = getToken();
        const res = await fetch(`${API_BASE_URL}/checklists/${selectedChecklist._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify(updates),
        });
        if (!res.ok) throw new Error('Failed to update');
        const data = await res.json();
        setSelectedChecklist(data);
        setChecklists(prev => prev.map(c => c._id === data._id ? data : c));
        return;
      } catch (err) {
        console.error('Error saving checklist:', err);
        syncChecklist(selectedChecklist._id);
        showToast('Failed to save checklist', 'error');
        return;
      }
    }

    showToast('Cannot save changes while offline. Please reconnect.', 'error');
    syncChecklist(selectedChecklist._id);
  };

  const doReorderChecklist = async (nextGroups) => {
    if (!selectedChecklist) return;

    // Optimistic local update
    const updatedChecklist = { ...selectedChecklist, groups: nextGroups, updatedAt: Date.now() };
    setSelectedChecklist(updatedChecklist);
    setChecklists(prev => prev.map(c => c._id === updatedChecklist._id ? updatedChecklist : c));

    if (!onlineDetector.isOnline) {
      showToast('Cannot reorder while offline. Please reconnect.', 'error');
      syncChecklist(selectedChecklist._id);
      return;
    }

    try {
      const token = getToken();
      const orderData = {
        groups: nextGroups.map(g => ({
          _id: g._id,
          items: (g.items || []).map(i => i._id),
        })),
      };
      const res = await fetch(`${API_BASE_URL}/checklists/${selectedChecklist._id}/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(orderData),
      });
      if (!res.ok) throw new Error('Failed to reorder');
      const data = await res.json();
      setSelectedChecklist(data);
      setChecklists(prev => prev.map(c => c._id === data._id ? data : c));
      showToast('Order saved', 'success');
    } catch (err) {
      console.error('Error reordering checklist:', err);
      syncChecklist(selectedChecklist._id);
      showToast('Failed to save order', 'error');
    }
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    if (!selectedChecklist) return;

    const groups = selectedChecklist.groups || [];

    // ── Dragging a group ────────────────────────────────────────
    if (groups.some(g => String(g._id) === String(active.id))) {
      const fromIdx = groups.findIndex(g => String(g._id) === String(active.id));
      let toIdx = groups.findIndex(g => String(g._id) === String(over.id));
      // Over an item inside another group → target that group
      if (toIdx === -1) {
        const overGroup = groups.find(g => (g.items || []).some(i => String(i._id) === String(over.id)));
        toIdx = overGroup ? groups.findIndex(g => String(g._id) === String(overGroup._id)) : -1;
      }
      if (fromIdx === -1 || toIdx === -1) return;
      doReorderChecklist(arrayMove(groups, fromIdx, toIdx));
      return;
    }

    // ── Dragging an item ────────────────────────────────────────
    const activeGroupIdx = groups.findIndex(g => (g.items || []).some(i => String(i._id) === String(active.id)));
    if (activeGroupIdx === -1) return;

    let overGroupIdx = groups.findIndex(g => String(g._id) === String(over.id));
    let targetIdx = -1;
    if (overGroupIdx === -1) {
      // Over another item → position relative to it
      overGroupIdx = groups.findIndex(g => (g.items || []).some(i => String(i._id) === String(over.id)));
      if (overGroupIdx !== -1) {
        targetIdx = groups[overGroupIdx].items.findIndex(i => String(i._id) === String(over.id));
      }
    }
    if (overGroupIdx === -1) return;

    const nextGroups = groups.map(g => ({ ...g, items: [...g.items] }));
    const fromIdx = nextGroups[activeGroupIdx].items.findIndex(i => String(i._id) === String(active.id));
    if (fromIdx === -1) return;

    if (activeGroupIdx === overGroupIdx) {
      // Reorder within the same group
      if (targetIdx === -1) return;
      nextGroups[activeGroupIdx].items = arrayMove(nextGroups[activeGroupIdx].items, fromIdx, targetIdx);
    } else {
      // Move item to another group (drop on group header = append to end)
      const [moved] = nextGroups[activeGroupIdx].items.splice(fromIdx, 1);
      if (targetIdx === -1) targetIdx = nextGroups[overGroupIdx].items.length;
      nextGroups[overGroupIdx].items.splice(targetIdx, 0, moved);
    }

    doReorderChecklist(nextGroups);
  };

  const handleDuplicate = async () => {
    if (!selectedChecklist) return;

    if (!onlineDetector.isOnline) {
      showToast('Cannot duplicate checklist while offline. Please reconnect.', 'error');
      return;
    }

    try {
      const token = getToken();
      const duplicateData = {
        title: `${selectedChecklist.title} (Copy)`,
        groups: selectedChecklist.groups.map(g => ({
          name: g.name, icon: g.icon,
          items: g.items.map(i => ({ name: i.name, qty: i.qty, note: i.note })),
        })),
      };
      const res = await fetch(`${API_BASE_URL}/checklists`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(duplicateData),
      });
      if (!res.ok) throw new Error('Failed to duplicate');
      const data = await res.json();
      setChecklists(prev => [data, ...prev]);
      showToast('Duplicated', 'success');
    } catch (err) {
      console.error('Error duplicating checklist:', err);
      showToast(err.message, 'error');
    }
  };

  // ─── Groups ───────────────────────────────────────────────────

  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId); else next.add(groupId);
      return next;
    });
  };

  const openAddGroup = () => {
    setEditingGroup(null);
    setGroupForm({ name: '', icon: '' });
    setShowGroupModal(true);
  };

  const openEditGroup = (group) => {
    setEditingGroup(group);
    setGroupForm({ name: group.name, icon: group.icon || '' });
    setShowGroupModal(true);
  };

  const handleSaveGroup = async () => {
    if (!groupForm.name.trim()) { showToast('Group name required', 'error'); return; }

    if (!onlineDetector.isOnline) {
      showToast('Cannot modify groups while offline. Please reconnect.', 'error');
      return;
    }

    const groupData = { name: groupForm.name.trim(), icon: groupForm.icon || '' };

    try {
      const token = getToken();
      const url = editingGroup
        ? `${API_BASE_URL}/checklists/${selectedChecklist._id}/groups/${editingGroup._id}`
        : `${API_BASE_URL}/checklists/${selectedChecklist._id}/groups`;
      const res = await fetch(url, {
        method: editingGroup ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(groupData),
      });
      if (!res.ok) throw new Error('Failed to save group');
      await syncChecklist(selectedChecklist._id);
      setShowGroupModal(false);
      showToast(editingGroup ? 'Group updated' : 'Group added', 'success');
    } catch (err) {
      console.error('Error saving group:', err);
      showToast(err.message, 'error');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot delete while offline. Please reconnect.', 'error');
      return;
    }
    const confirmed = await confirm('Delete this group?', 'All items will be removed.');
    if (!confirmed) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/checklists/${selectedChecklist._id}/groups/${groupId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      await syncChecklist(selectedChecklist._id);
      showToast('Group deleted', 'success');
    } catch (err) { showToast(err.message, 'error'); }
  };

  // ─── Items ────────────────────────────────────────────────────

  const openAddItem = (groupId) => {
    setActiveGroupId(groupId);
    setEditingItem(null);
    setItemForm({ name: '', qty: 1, note: '' });
    setShowItemModal(true);
  };

  const openEditItem = (groupId, item) => {
    setActiveGroupId(groupId);
    setEditingItem(item);
    setItemForm({ name: item.name, qty: item.qty, note: item.note || '' });
    setShowItemModal(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) { showToast('Item name required', 'error'); return; }

    if (!onlineDetector.isOnline) {
      showToast('Cannot modify items while offline. Please reconnect.', 'error');
      return;
    }

    const itemData = { name: itemForm.name.trim(), qty: itemForm.qty, note: itemForm.note || '' };

    try {
      const token = getToken();
      const url = editingItem
        ? `${API_BASE_URL}/checklists/${selectedChecklist._id}/groups/${activeGroupId}/items/${editingItem._id}`
        : `${API_BASE_URL}/checklists/${selectedChecklist._id}/groups/${activeGroupId}/items`;
      const res = await fetch(url, {
        method: editingItem ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify(itemData),
      });
      if (!res.ok) throw new Error('Failed');
      await syncChecklist(selectedChecklist._id);
      setShowItemModal(false);
      showToast(editingItem ? 'Item updated' : 'Item added', 'success');
    } catch (err) {
      console.error('Error saving item:', err);
      showToast(err.message, 'error');
    }
  };

  const handleToggleItem = async (groupId, itemId, currentCompleted) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot toggle items while offline. Please reconnect.', 'error');
      return;
    }

    const newCompleted = !currentCompleted;

    // Optimistic local update
    setSelectedChecklist(prev => {
      if (!prev) return prev;
      const updatedGroups = prev.groups.map(g => {
        if (g._id !== groupId) return g;
        return {
          ...g,
          items: g.items.map(i => i._id === itemId ? { ...i, completed: newCompleted } : i)
        };
      });
      return { ...prev, groups: updatedGroups };
    });

    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/checklists/${selectedChecklist._id}/groups/${groupId}/items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ completed: newCompleted }),
      });
      if (!res.ok) throw new Error('Failed');
      await syncChecklist(selectedChecklist._id);
      return;
    } catch (err) {
      console.error('Error toggling item:', err);
      setSelectedChecklist(prev => {
        if (!prev) return prev;
        const updatedGroups = prev.groups.map(g => {
          if (g._id !== groupId) return g;
          return {
            ...g,
            items: g.items.map(i => i._id === itemId ? { ...i, completed: currentCompleted } : i)
          };
        });
        return { ...prev, groups: updatedGroups };
      });
      showToast('Failed to update item', 'error');
      return;
    }
  };

  const handleDeleteItem = async (groupId, itemId) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot delete while offline. Please reconnect.', 'error');
      return;
    }
    const confirmed = await confirm('Delete this item?', 'This item will be removed.');
    if (!confirmed) return;
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE_URL}/checklists/${selectedChecklist._id}/groups/${groupId}/items/${itemId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed');
      await syncChecklist(selectedChecklist._id);
      showToast('Item deleted', 'success');
    } catch (err) { showToast(err.message, 'error'); }
  };

  const handleToggleGroup = async (groupId, items, allCompleted) => {
    if (!onlineDetector.isOnline) {
      showToast('Cannot toggle items while offline. Please reconnect.', 'error');
      return;
    }

    if (!Array.isArray(items) || items.length === 0) return;
    const newCompleted = !allCompleted;

    // Optimistic local update for the whole group
    setSelectedChecklist(prev => {
      if (!prev) return prev;
      const updatedGroups = prev.groups.map(g => {
        if (g._id !== groupId) return g;
        return {
          ...g,
          items: g.items.map(i => ({ ...i, completed: newCompleted })),
        };
      });
      return { ...prev, groups: updatedGroups };
    });

    try {
      const token = getToken();
      const results = await Promise.allSettled(items.map(item =>
        fetch(`${API_BASE_URL}/checklists/${selectedChecklist._id}/groups/${groupId}/items/${item._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          credentials: 'include',
          body: JSON.stringify({ completed: newCompleted }),
        })
      ));

      const anyFailed = results.some(r =>
        r.status === 'rejected' || (r.status === 'fulfilled' && !r.value.ok)
      );

      if (anyFailed) {
        await syncChecklist(selectedChecklist._id);
        showToast('Failed to update group items', 'error');
        return;
      }

      await syncChecklist(selectedChecklist._id);
      showToast(newCompleted ? 'All items completed' : 'All items unmarked', 'success');
    } catch (err) {
      console.error('Error toggling group items:', err);
      await syncChecklist(selectedChecklist._id);
      showToast('Failed to update group items', 'error');
    }
  };

  const filtered = checklists.filter(c => {
    if ((c.archived === undefined ? false : c.archived) !== showArchived) return false;
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    if (c.title.toLowerCase().includes(q)) return true;
    return c.groups?.some(g =>
      g.name.toLowerCase().includes(q) ||
      g.items?.some(i => i.name.toLowerCase().includes(q))
    );
  });

  const tot = totalItems;
  const comp = completedItems;
  const pct = tot > 0 ? Math.round((comp / tot) * 100) : 0;

  return (
    <div className="checklist-page">
      {/* Header */}
      <PageHero
        icon={<FaClipboardList />}
        title="Checklists"
        subtitle="Organize items for any event or project"
        actions={
          <button className="page-hero__btn" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> New Checklist
          </button>
        }
      />

      {/* Search & Filters */}
      <div className="checklist-page__toolbar">
        <div className="checklist-page__search">
          <FaSearch className="checklist-page__search-icon" />
          <input
            type="text" className="checklist-page__search-input" placeholder="Search checklists..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="checklist-page__search-clear" onClick={() => setSearchTerm('')}><FaTimes /></button>
          )}
        </div>
        <div className="checklist-page__tabs">
          <button className={`checklist-page__tab ${!showArchived ? 'active' : ''}`} onClick={() => setShowArchived(false)}>Active</button>
          <button className={`checklist-page__tab ${showArchived ? 'active' : ''}`} onClick={() => setShowArchived(true)}><FaArchive /> Archived</button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="checklist__skeleton-grid">
          {[1,2,3,4,5,6].map(i => (
            <div key={i} className="checklist__skeleton-card" style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="skeleton-shimmer" style={{ height: '20px', width: '75%', borderRadius: '6px' }} />
              <div className="skeleton-shimmer" style={{ height: '12px', width: '45%', marginTop: '12px', borderRadius: '6px' }} />
              <div className="checklist__skeleton-bar skeleton-shimmer" />
              <div className="skeleton-shimmer" style={{ height: '10px', width: '55%', marginTop: '16px', borderRadius: '6px' }} />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="checklist__empty">
          <div className="checklist__empty-icon">📋</div>
          <h3>{showArchived ? 'No archived checklists' : 'No checklists yet'}</h3>
          <p>{showArchived ? 'Archived checklists will appear here.' : 'Create your first checklist.'}</p>
          {!showArchived && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}><FaPlus /> Create Checklist</button>
          )}
        </div>
      ) : (
        <div className="checklist-page__grid">
          {filtered.map((checklist, index) => {
            const { tot, comp, pct } = computeProgress(checklist);
            const itemCount = checklist.groups?.reduce((s, g) => s + (g.items?.length || 0), 0) || 0;
            const groupCount = checklist.groups?.length || 0;
            return (
              <div
                key={checklist._id}
                data-checklist-id={checklist._id}
                className={`checklist-card ${checklist.pinned ? 'checklist-card--pinned' : ''} ${highlightedChecklistId === String(checklist._id) ? 'checklist-card--highlight' : ''}`}
                onClick={() => openViewModal(checklist)}
                style={{ animationDelay: `${index * 0.04}s`, ...(highlightedChecklistId === String(checklist._id) ? { boxShadow: '0 0 0 3px #6366f1, 0 8px 24px rgba(99,102,241,0.3)' } : {}) }}
              >
                {checklist.pinned && <FaThumbtack className="checklist-card__pin-badge" />}
                <div className="checklist-card__header">
                  <h3 className="checklist-card__title">{checklist.title}</h3>
                </div>
                <div className="checklist-card__stats">
                  <span>{groupCount} {groupCount === 1 ? 'group' : 'groups'}</span>
                  <span>{itemCount} {itemCount === 1 ? 'item' : 'items'}</span>
                </div>
                {itemCount > 0 && (
                  <div className="checklist-card__progress">
                    <div className="checklist-card__progress-bar">
                      <div className="checklist-card__progress-fill" style={{ width: `${pct}%` }} />
                    </div>
                    <span className="checklist-card__progress-text">{comp}/{tot}</span>
                  </div>
                )}
                <div className="checklist-card__footer">
                  <span className="checklist-card__date">
                    {formatDateRelative(checklist.updatedAt || checklist.createdAt)}
                  </span>
                  <div className="checklist-card__actions">
                    <button className="checklist-card__action" onClick={(e) => openRename(checklist._id, checklist.title, e)} title="Rename"><FaEdit /></button>
                    <button className="checklist-card__action" onClick={(e) => handlePinToggle(checklist._id, e)} title={checklist.pinned ? 'Unpin' : 'Pin'}><FaThumbtack /></button>
                    <button className={`checklist-card__action ${checklist.archived ? 'checklist-card__action--archived' : ''}`} onClick={(e) => handleArchiveToggle(checklist._id, e)} title={checklist.archived ? 'Restore' : 'Archive'}><FaArchive /></button>
                    <button className="checklist-card__action checklist-card__action--danger" onClick={(e) => handleDelete(checklist._id, e)} title="Delete"><FaTrash /></button>
                    <FaChevronRight className="checklist-card__arrow" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Create Modal ──────────────────────────────────────────── */}
      <Modal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} title="New Checklist" size="small">
        <div className="checklist-modal__form">
          <div className="form-group">
            <label>Checklist Title *</label>
            <input type="text" placeholder="e.g., Wedding, Event, Project"
              value={createForm.title} onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })} autoFocus />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreate}>Create Checklist</button>
          </div>
        </div>
      </Modal>

      {/* ── Rename Modal ──────────────────────────────────────────── */}
      <Modal isOpen={showRenameModal} onClose={() => setShowRenameModal(false)} title="Rename Checklist" size="small">
        <div className="checklist-modal__form">
          <div className="form-group">
            <label>Checklist Title *</label>
            <input type="text" value={renameTitle}
              onChange={(e) => setRenameTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRename(); }} autoFocus />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowRenameModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleRename}>Rename</button>
          </div>
        </div>
      </Modal>

      {/* ── View Modal (Detail) ──────────────────────────────────── */}
      <Modal
        isOpen={showViewModal}
        onClose={closeViewModal}
        title={selectedChecklist?.title || 'Checklist'}
        size="medium"
        headerActions={
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button className="btn btn-primary" onClick={openAddGroup} style={{ padding: '0.4rem 0.75rem', fontSize: '0.85rem' }}>
              <FaPlus /> <span className="hide-mobile">Add Group</span>
            </button>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-secondary" onClick={() => setShowActionsMenu(!showActionsMenu)}
                style={{ padding: '0.4rem 0.65rem' }}><FaEllipsisV /></button>
              {showActionsMenu && (
                <>
                  <div className="checklist__overlay" onClick={() => setShowActionsMenu(false)} />
                  <div className="checklist-detail__actions-menu">
                    <button onClick={() => { setShowActionsMenu(false); setRenamingId(selectedChecklist._id); setRenameTitle(selectedChecklist.title); setShowRenameModal(true); }}><FaEdit /> Rename</button>
                    <button onClick={() => { setShowActionsMenu(false); handlePinToggle(selectedChecklist._id); }}>
                      <FaThumbtack /> {selectedChecklist?.pinned ? 'Unpin' : 'Pin'}
                    </button>
                    <button onClick={() => { setShowActionsMenu(false); handleArchiveToggle(selectedChecklist._id); }}>
                      <FaArchive /> {selectedChecklist?.archived ? 'Restore' : 'Archive'}
                    </button>
                    <button onClick={() => { setShowActionsMenu(false); handleDuplicate(); }}><FaCopy /> Duplicate</button>
                    <button className="danger" onClick={() => { setShowActionsMenu(false); handleDelete(selectedChecklist._id, null); }}><FaTrash /> Delete</button>
                  </div>
                </>
              )}
            </div>
          </div>
        }
      >
        {selectedChecklist && (
          <div className="checklist-detail">
            {/* Progress */}
            {tot > 0 && (
              <div className="checklist-detail__progress">
                <div className="checklist-detail__progress-header">
                  <span className="checklist-detail__progress-label">Progress</span>
                  <span className="checklist-detail__progress-count">{comp} / {tot}</span>
                </div>
                <div className="checklist-detail__progress-bar">
                  <div className="checklist-detail__progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="checklist-detail__progress-stats">
                  <span className="checklist-detail__progress-stat checklist-detail__progress-stat--done">
                    <FaCheck /> {comp} Done
                  </span>
                  <span className="checklist-detail__progress-stat checklist-detail__progress-stat--pending">
                    {tot - comp} Pending
                  </span>
                  <span className="checklist-detail__progress-stat">{pct}%</span>
                </div>
              </div>
            )}

            {/* Groups */}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <div className="checklist-detail__groups">
                {selectedChecklist.groups.length === 0 && (
                  <div className="checklist__empty" style={{ padding: '2rem' }}>
                    <div className="checklist__empty-icon">📋</div>
                    <h3>No groups yet</h3>
                    <p>Create groups to organize items.</p>
                    <button className="btn btn-primary" onClick={openAddGroup}><FaPlus /> Create Group</button>
                  </div>
                )}

                <SortableContext
                  id="checklist-groups"
                  items={selectedChecklist.groups.map(g => g._id)}
                  strategy={verticalListSortingStrategy}
                >
                  {selectedChecklist.groups.map((group) => {
                    const isCollapsed = collapsedGroups.has(group._id);
                    const groupTot = group.items?.length || 0;
                    const groupComp = group.items?.filter(i => i.completed)?.length || 0;

                    return (
                      <SortableGroupShell key={group._id} id={group._id}>
                        {({ attributes, listeners }) => (
                          <>
                            <div className="checklist-detail__group-header" onClick={() => toggleGroupCollapse(group._id)}>
                              <button
                                className="checklist-detail__group-drag"
                                title="Drag to reorder"
                                {...attributes}
                                {...listeners}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FaGripVertical />
                              </button>
                              {group.items?.length > 0 && (
                                <button
                                  className={`checklist-detail__group-select ${groupTot > 0 && groupComp === groupTot ? 'checklist-detail__group-select--checked' : ''} ${groupComp > 0 && groupComp < groupTot ? 'checklist-detail__group-select--partial' : ''}`}
                                  onClick={(e) => { e.stopPropagation(); handleToggleGroup(group._id, group.items, groupTot > 0 && groupComp === groupTot); }}
                                  title={groupTot > 0 && groupComp === groupTot ? 'Deselect all' : 'Select all'}
                                >
                                  {groupTot > 0 && groupComp === groupTot && <FaCheck />}
                                </button>
                              )}
                              <button className="checklist-detail__group-collapse" onClick={(e) => { e.stopPropagation(); toggleGroupCollapse(group._id); }}>
                                {isCollapsed ? <FaChevronRight /> : <FaChevronDown />}
                              </button>
                              <span className="checklist-detail__group-icon">{group.icon || '📋'}</span>
                              <span className="checklist-detail__group-name">{group.name}</span>
                              <span className="checklist-detail__group-count">
                                {groupComp}/{groupTot}
                                {groupTot > 0 && groupComp === groupTot && <span className="checklist-detail__group-done"> ✓</span>}
                              </span>
                              <div className="checklist-detail__group-actions" onClick={(e) => e.stopPropagation()}>
                                <button className="checklist-detail__group-action" onClick={() => openEditGroup(group)} title="Edit"><FaEdit /></button>
                                <button className="checklist-detail__group-action" onClick={() => handleDeleteGroup(group._id)} title="Delete"><FaTrash /></button>
                              </div>
                            </div>

                            <div className={`checklist-detail__group-body ${isCollapsed ? 'checklist-detail__group-body--collapsed' : 'checklist-detail__group-body--expanded'}`}>
                              {group.items?.length === 0 && (
                                <div className="checklist-detail__group-empty"><p>No items yet.</p></div>
                              )}
                              <SortableContext
                                id={`checklist-group-${group._id}`}
                                items={(group.items || []).map(i => i._id)}
                                strategy={verticalListSortingStrategy}
                              >
                                {group.items?.map((item) => (
                                  <SortableItemShell
                                    key={item._id}
                                    id={item._id}
                                    className={`checklist-detail__item ${item.completed ? 'checklist-detail__item--done' : ''}`}
                                  >
                                    {({ attributes, listeners }) => (
                                      <>
                                        <button
                                          className="checklist-detail__item-drag"
                                          title="Drag to reorder"
                                          {...attributes}
                                          {...listeners}
                                        >
                                          <FaGripVertical />
                                        </button>
                                        <button
                                          className={`checklist-detail__item-checkbox ${item.completed ? 'checklist-detail__item-checkbox--checked' : ''}`}
                                          onClick={() => handleToggleItem(group._id, item._id, item.completed)}
                                        >
                                          {item.completed && <FaCheck />}
                                        </button>
                                        <div className="checklist-detail__item-info" onClick={() => handleToggleItem(group._id, item._id, item.completed)}>
                                          <span className="checklist-detail__item-name">{item.name}</span>
                                          <div className="checklist-detail__item-meta">
                                            <span className="checklist-detail__item-qty"><FaCopy /> ×{item.qty}</span>
                                            {item.note && <span className="checklist-detail__item-note">{item.note}</span>}
                                          </div>
                                        </div>
                                        <div className="checklist-detail__item-actions">
                                          <button className="checklist-detail__item-action" onClick={() => openEditItem(group._id, item)} title="Edit"><FaEdit /></button>
                                          <button className="checklist-detail__item-action" onClick={() => handleDeleteItem(group._id, item._id)} title="Delete"><FaTimes /></button>
                                        </div>
                                      </>
                                    )}
                                  </SortableItemShell>
                                ))}
                              </SortableContext>
                              <button className="checklist-detail__add-item-btn" onClick={() => openAddItem(group._id)}>
                                <FaPlus /> Add Item
                              </button>
                            </div>
                          </>
                        )}
                      </SortableGroupShell>
                    );
                  })}
                </SortableContext>
              </div>
            </DndContext>
          </div>
        )}
      </Modal>

      {/* ── Group Modal ───────────────────────────────────────────── */}
      <Modal isOpen={showGroupModal} onClose={() => setShowGroupModal(false)}
        title={editingGroup ? 'Edit Group' : 'Create Group'} size="small">
        <div className="checklist-modal__form">
          <div className="form-group">
            <label>Group Name *</label>
            <input type="text" placeholder="e.g., Camera Equipment"
              value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} autoFocus />
          </div>
          <div className="form-group">
            <label>Icon (Optional)</label>
            <div className="checklist-modal__icon-picker">
              {GROUP_ICONS.map(icon => (
                <button key={icon}
                  className={`checklist-modal__icon-btn ${groupForm.icon === icon ? 'active' : ''}`}
                  onClick={() => setGroupForm({ ...groupForm, icon: groupForm.icon === icon ? '' : icon })}>
                  {icon}
                </button>
              ))}
            </div>
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowGroupModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveGroup}>
              {editingGroup ? 'Update' : 'Create'} Group
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Item Modal ────────────────────────────────────────────── */}
      <Modal isOpen={showItemModal} onClose={() => setShowItemModal(false)}
        title={editingItem ? 'Edit Item' : 'Add Item'} size="small">
        <div className="checklist-modal__form">
          <div className="form-group">
            <label>Item Name *</label>
            <input type="text" placeholder="e.g., Camera Battery"
              value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} autoFocus />
          </div>
          <div className="form-group">
            <label>Quantity</label>
            <div className="checklist-modal__qty-input">
              <button className="checklist-modal__qty-btn"
                onClick={() => setItemForm({ ...itemForm, qty: Math.max(1, itemForm.qty - 1) })}
                disabled={itemForm.qty <= 1}><FaMinus /></button>
              <input type="number" min="1" value={itemForm.qty}
                onChange={(e) => setItemForm({ ...itemForm, qty: Math.max(1, parseInt(e.target.value) || 1) })} />
              <button className="checklist-modal__qty-btn"
                onClick={() => setItemForm({ ...itemForm, qty: itemForm.qty + 1 })}><FaPlus /></button>
            </div>
          </div>
          <div className="form-group">
            <label>Note (Optional)</label>
            <textarea placeholder="e.g., Fully charged before event"
              value={itemForm.note} onChange={(e) => setItemForm({ ...itemForm, note: e.target.value })} rows={3} />
          </div>
          <div className="form-actions">
            <button className="btn btn-secondary" onClick={() => setShowItemModal(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSaveItem}>
              {editingItem ? 'Update' : 'Add'} Item
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ChecklistList;
